import React from 'react';
import { CanvasObject as CanvasObjectType } from '../types';
import { intToHex } from '../utils';

interface CanvasObjectProps {
    obj: CanvasObjectType;
    isSelected: boolean;
    isMultiSelected: boolean;
    onMouseDown: (e: React.MouseEvent, objectId: string) => void;
    onMouseMove: (e: React.MouseEvent) => void;
    onResizeStart: (e: React.MouseEvent, objectId: string, handle: string) => void;
    isPreview?: boolean;
}

export const CanvasObjectComponent: React.FC<CanvasObjectProps> = ({
    obj,
    isSelected,
    isMultiSelected,
    onMouseDown,
    onMouseMove,
    onResizeStart,
    isPreview = false
}) => {
    // Helper to safely get color property
    const getColor = (colorInt?: number) => {
        return intToHex(colorInt);
    };

    const style: React.CSSProperties = {
        position: 'absolute',
        left: obj.position.x,
        top: obj.position.y,
        width: obj.size.width,
        height: obj.size.height,
        backgroundColor: obj.type !== 'text' && obj.type !== 'arrow' && obj.type !== 'image' ? getColor(obj.color) : 'transparent',
        color: obj.type === 'text' ? getColor(obj.borderColor) : 'inherit',


        border: (obj.type === 'rectangle' || obj.type === 'circle' || obj.type === 'image') && obj.borderWidth ? `${obj.borderWidth}px solid ${getColor(obj.borderColor)}` : 'none',
        borderRadius: obj.type === 'circle' ? '50%' : '0px',
        opacity: obj.opacity ?? 1,
        zIndex: obj.zIndex ?? 0,
        transform: `rotate(${obj.rotation || 0}deg)`,
    };

    const getFontFamily = (fontFamily?: string) => {
        if (!fontFamily) return 'Roboto, sans-serif';
        const fallbacks: Record<string, string> = {
            DancingScript: 'cursive',
            Pacifico: 'cursive',
            Inconsolata: 'monospace',
            Montserrat: 'sans-serif',
            Oswald: 'sans-serif',
            ArchivoBlack: 'sans-serif',
            BebasNeue: 'sans-serif',
            JosefinSans: 'sans-serif'
        };
        const fallback = fallbacks[fontFamily] || 'sans-serif';
        return `"${fontFamily}", ${fallback}`;
    };

    const getFontWeight = (fontWeightIndex?: number) => {
        // Map 0-8 to 100-900 
        const index = fontWeightIndex ?? 3; // 3 is normal (400)
        return (Math.max(0, Math.min(8, index)) + 1) * 100;
    };

    let content;
    switch (obj.type) {
        case 'text':
            content = (
                <div
                    style={{
                        fontSize: obj.fontSize,
                        fontFamily: getFontFamily(obj.fontFamily),
                        fontWeight: getFontWeight(obj.fontWeight),
                        textAlign: ['left', 'right', 'center', 'justify'][obj.textAlign || 0] as any, // Map index
                        fontStyle: obj.fontStyle === 1 ? 'italic' : 'normal', // Map index
                        textDecoration: obj.isUnderlined ? 'underline' : 'none',
                        width: '100%',
                        height: '100%',
                        color: getColor(obj.color), // Base color as text color
                        border: obj.borderWidth ? `${obj.borderWidth}px solid ${getColor(obj.borderColor)}` : 'none',
                        wordWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                        overflow: 'hidden'
                    }}
                >
                    {obj.text}
                </div>
            );
            break;
        case 'arrow':
            const markerEndId = `arrowhead-end-${obj.id}`;
            const markerStartId = `arrowhead-start-${obj.id}`;
            const markerWidth = 10;
            const markerHeight = 7;
            const strokeColor = getColor(obj.color);
            const strokeWidth = obj.strokeWidth || 2;

            const isDouble = obj.arrowType === 'double';
            const dashArray = obj.lineStyle === 'dashed' ? '5,5' : obj.lineStyle === 'dotted' ? '2,2' : undefined;

            const w = obj.size.width;
            const h = obj.size.height;
            const midY = h / 2;
            const headSize = 10;

            let pathData = '';
            const isCurve = obj.arrowStyle === 'curve';
            const isElbow = obj.arrowStyle === 'elbow';

            if (obj.hasBreakpoint) {
                if (isCurve) {
                    pathData = `M 0 ${midY} Q ${w / 2} 0 ${w} ${midY}`;
                } else {
                    pathData = `M 0 ${midY} L ${w / 2} 0 L ${w} ${midY}`;
                }
            } else {
                if (isCurve) {
                    pathData = `M 0 ${midY} C ${w / 3} ${midY}, ${(2 * w) / 3} ${midY}, ${w} ${midY}`;
                } else if (isElbow) {
                    pathData = `M 0 ${midY} L ${w / 2} ${midY} L ${w / 2} ${midY} L ${w} ${midY}`;
                } else {
                    pathData = `M 0 ${midY} L ${w - 5} ${midY}`;
                }
            }

            content = (
                <svg width={w} height={h} style={{ overflow: 'visible' }}>
                    <path
                        d={pathData}
                        stroke={strokeColor}
                        strokeWidth={strokeWidth}
                        fill="none"
                        strokeDasharray={dashArray}
                    />
                    {/* Render arrowheads as explicit polygons for html2canvas compatibility */}
                    <polygon
                        points={`${w - headSize},${midY - 5} ${w},${midY} ${w - headSize},${midY + 5}`}
                        fill={strokeColor}
                    />
                    {isDouble && (
                        <polygon
                            points={`${headSize},${midY - 5} 0,${midY} ${headSize},${midY + 5}`}
                            fill={strokeColor}
                        />
                    )}
                </svg>
            );
            break;
        case 'image':
            content = obj.imageUrl ? (
                <div style={{
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <img
                        src={obj.imageUrl}
                        alt={'Canvas Image'}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                        }}
                        draggable={false}
                    />
                </div>
            ) : (
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#f0f0f0',
                        color: '#999',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    No Image
                </div>
            );
            break;
    }

    if (isPreview) {
        return (
            <div key={obj.id} style={style}>
                {content}
            </div>
        );
    }

    return (
        <div
            key={obj.id}
            className={`canvas-object ${isSelected ? (isMultiSelected ? 'multi-selected' : 'selected') : ''}`}
            style={style}
            onMouseDown={(e) => onMouseDown(e, obj.id)}
            onMouseMove={onMouseMove}
        >
            {content}
            {isSelected && !isMultiSelected && (
                <>
                    <div className="resize-handle nw" onMouseDown={(e) => onResizeStart(e, obj.id, 'nw')} />
                    <div className="resize-handle ne" onMouseDown={(e) => onResizeStart(e, obj.id, 'ne')} />
                    <div className="resize-handle sw" onMouseDown={(e) => onResizeStart(e, obj.id, 'sw')} />
                    <div className="resize-handle se" onMouseDown={(e) => onResizeStart(e, obj.id, 'se')} />
                </>
            )}
        </div>
    );
};